import { ArrowRight, ChevronRight, FileText, CircleQuestionMark as HelpCircle, Layers } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
export default function BasementCta() {
  return <section id="basementremodelingcta" data-nav="light" className="bg-background text-foreground py-16 sm:py-24 transition-colors">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-card text-card-foreground border border-border rounded-2xl shadow-xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
            {/* Left Content Column */}
            <div className="lg:col-span-7 p-8 sm:p-12 lg:p-14 flex flex-col justify-between">
              <div>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground leading-tight">
                  Ready to transform your lower level into a refined living space?
                </h2>
                <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-xl">
                  Connect with Brookhaven Elevation Renovations to plan your custom entertainment suite, basement guest quarters, or tailored subterranean remodel.
                </p>

                {/* Primary CTA */}
                <div className="mt-8">
                  <Button asChild size="lg" className="px-7 py-3 text-base shadow-md group">
                    <Link to="/ContactRequestAnEstimatePage">
                      <span>Request an Estimate</span>
                      <ArrowRight className="size-4 ml-1 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </div>

              {/* Secondary Navigation & Resource Links */}
              <div className="mt-12 pt-8 border-t border-border">
                {/* Related Services Row */}
                <div className="mb-6">
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-3">
                    Related Services
                  </span>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    <Link to="/HomeAdditionsPage" className="inline-flex items-center text-sm font-medium text-foreground hover:text-primary transition-colors group">
                      <Layers className="size-3.5 mr-1.5 text-muted-foreground group-hover:text-primary transition-colors" />
                      Home Additions
                      <ChevronRight className="size-3 ml-0.5 opacity-60 group-hover:opacity-100 transition-opacity" />
                    </Link>
                    <Separator orientation="vertical" className="h-3.5 hidden sm:block" />
                    <Link to="/WholeHomeInteriorRemodelingPage" className="inline-flex items-center text-sm font-medium text-foreground hover:text-primary transition-colors group">
                      <Layers className="size-3.5 mr-1.5 text-muted-foreground group-hover:text-primary transition-colors" />
                      Whole-Home Remodeling
                      <ChevronRight className="size-3 ml-0.5 opacity-60 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  </div>
                </div>

                {/* Planning & FAQ Links */}
                <div>
                  <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-3">
                    Remodeling Resources
                  </span>
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                    <Link to="#" className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors">
                      <FileText className="size-3.5 mr-1.5" />
                      Remodeling Cost Guide
                    </Link>
                    <Separator orientation="vertical" className="h-3.5 hidden sm:block" />
                    <Link to="#" className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors">
                      <FileText className="size-3.5 mr-1.5" />
                      Remodeling Planning Guide
                    </Link>
                    <Separator orientation="vertical" className="h-3.5 hidden sm:block" />
                    <Link to="#" className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors">
                      <HelpCircle className="size-3.5 mr-1.5" />
                      FAQ
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Visual Column */}
            <div className="lg:col-span-5 bg-muted/40 p-6 sm:p-8 lg:p-10 flex flex-col justify-center border-t lg:border-t-0 lg:border-l border-border">
              <div className="grid grid-cols-1 gap-4">
                <div className="relative rounded-lg overflow-hidden border border-border shadow-xs group">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_remodel_custom_lounge.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_remodel_custom_lounge.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_remodel_custom_lounge.png-wvc-sizes" alt="Finished luxury basement renovation with modern recessed lighting, wet bar, and custom built-in cabinetry in Brookhaven" loading="lazy" decoding="async" className="w-full h-56 sm:h-64 object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                  <span className="absolute bottom-3 left-3 text-xs font-medium text-white px-2 py-0.5 rounded-sm bg-black/40 backdrop-blur-xs">
                    Finished Entertainment Lounge
                  </span>
                </div>

                <div className="relative rounded-lg overflow-hidden border border-border shadow-xs group">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_remodel_bar_suite.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_remodel_bar_suite.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/basement_remodel_bar_suite.png-wvc-sizes" alt="Custom walk-out basement wet bar with quartz counters, open timber shelving, and beverage center" loading="lazy" decoding="async" className="w-full h-44 sm:h-52 object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                  <span className="absolute bottom-3 left-3 text-xs font-medium text-white px-2 py-0.5 rounded-sm bg-black/40 backdrop-blur-xs">
                    Lower-Level Kitchenette & Bar
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}